// @flow

import React, { useState } from 'react';
import { difference, filter, flatten, includes, indexOf, sample } from 'lodash';
import { motion } from 'framer-motion';
import useWindowSize from '../utils/use_window_size';

type Props = {
  tile: Array<integer>,
  tileImages: Array<string>,
  dragEnd: Function,
  drag: Function,
  lastIndex: integer,
  reversed: boolean,
  responsive: boolean,
};

const newFirstIndex = (indices, existingTiles, lastIndex) => {
  if (lastIndex === null || includes(flatten(existingTiles), lastIndex)) {
    return sample(indices);
  }
  return lastIndex;
};

const newTile = (indices, existingTiles = [], lastIndex = null) => {
  const firstIndex = newFirstIndex(indices, existingTiles, lastIndex);
  const existingSecondIndices = filter(existingTiles,
    tile => tile[0] === firstIndex).map(tile => tile[1]);
  const secondIndex = sample(difference(indices, existingSecondIndices));
  return [firstIndex, secondIndex];
};

const rotation = orientation => ({ south: 90, north: -90 }[orientation] || 0);

const rotationOffset = (matchingIndex, currentRotation) => {
  if (matchingIndex === 0 && includes(['west', 'north'], currentRotation)) {
    return 180;
  }
  if (matchingIndex === 1 && includes(['east', 'south'], currentRotation)) {
    return 180;
  }
  return 0;
};

const Tile = ({ tile, tileImages, dragEnd, currentRotation, lastIndex, reversed, responsive }:
  Props) => {
  const [isDragging, setIsDragging] = useState(false);
  const matchingIndex = indexOf(tile, lastIndex);
  const windowSize = useWindowSize();

  return <motion.div
    drag={true}
    className="rounded overflow-hidden bg-white flex flex-row shadow-omni"
    animate={{
      rotate: isDragging ?
        rotation(currentRotation) + rotationOffset(matchingIndex, currentRotation) : 0,
      // Scale animation is related to the responsiveness (see img nodes below)
      scale: isDragging && responsive && windowSize.width <= 812 ? 1.625 : 1,
    }}
    onDragEnd={(e, info) => { setIsDragging(false); dragEnd(info.point); }}
    onDrag={() => { setIsDragging(true); }}
    dragElastic={1}
    dragConstraints={{
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    }}
  >
    <img
      className={`${responsive ? 'w-16 iphonex-landscape:w-16 narrow-mobile:w-16 md:w-26' : 'w-26'} mr-2 select-none pointer-events-none transform rotate-${reversed ? 180 : 0}`}
      src={tileImages[tile[0]]}
      alt={tile[0]}
    />
    <img
      className={`${responsive ? 'w-16 iphonex-landscape:w-16 narrow-mobile:w-16 md:w-26' : 'w-26'} select-none pointer-events-none transform rotate-${reversed ? 180 : 0}`}
      src={tileImages[tile[1]]}
      alt={tile[1]}
    />
  </motion.div>;
};

const EmptyTile = () => (
  <div
    className="rounded flex flex-row border-dashed bg-gray-300 bg-opacity-50 border-2 border-gray-600"
  >
    <div className="w-26 h-26" />
    <div className="w-26 h-26" />
  </div>
);

export { EmptyTile, newTile, Tile as default };
